// src/features/order/Coins.tsx
import React from 'react';
import { Input } from '../../../../../components/ui/input';
import { X, Equal } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux';
import { formatCashAmount, getCurrencySymbol } from '../../../../../lib/utils';
import { useTranslation } from 'react-i18next';
import { GenericCoin } from '../../../../../components/elements/GenericCoin';

type Props = {
  denom: Denom;
  onChangeInput: (coupure: CoupureType, qty: number) => void;
  coupures: CoupureInput[] | null;
  bulk?: boolean;
  currency?: string;
};

const Coins: React.FC<Props> = ({ denom, onChangeInput, coupures, bulk = false, currency = 'EUR' }) => {
  const symbol = getCurrencySymbol(currency);
  const { t } = useTranslation();
  const coupureConfig = useSelector((state: RootState) => state.coupure);

  const filterCoins = (coins: Coin[]) => {
    const filtered = coins
      .filter(coin =>
        coupureConfig.some(cfg => cfg.order === true && cfg.cn === 'C' && cfg.value === coin.coupure)
      )
      .sort((a, b) => b.coupure - a.coupure);
    console.log('[PlaceOrder][Coins] filter against coupureConfig', {
      input_count: coins.length,
      input_coupures: coins.map(c => c.coupure),
      filtered_count: filtered.length,
      filtered_coupures: filtered.map(c => c.coupure),
      coupureConfig_C_order_true: coupureConfig.filter(c => c.cn === 'C' && c.order === true).map(c => c.value),
    });
    return filtered;
  };

  const calculateSubtotal = (value: number, qty: number) => value * qty;

  const calculateTotal = () => {
    const total =
      coupures
        ?.filter(i => parseFloat(i.coupure) <= 2)
        .reduce((sum, i) => sum + i.qty * parseFloat(i.coupure), 0) || 0;
    return Math.round(total * 100) / 100;
  };

  const calculateWeight = () => {
    const total =
      coupures
        ?.filter(i => parseFloat(i.coupure) <= 2)
        .reduce((sum, i) => {
          const weight = coupureConfig.find(c => c.value === parseFloat(i.coupure))?.weight || 0;
          return sum + i.qty * weight;
        }, 0) || 0;
    return Math.round(total * 1000) / 1000;
  };

  return (
    <>
      <div className="flex flex-col gap-8">
        {filterCoins(denom.coins).map(coup => {
          const inputQty = coupures?.find(i => parseFloat(i.coupure) === coup.coupure)?.qty || 0;
          const displayValue = inputQty ? Math.round(inputQty / coup.perqty) : '';

          const maxAllowed = bulk
            ? coup.maxqty * (coupureConfig.find(c => c.value === coup.coupure)?.unit || 1)
            : coup.maxqty;

          const handleChange = (val: number) => {
            const clamped = Math.max(coup.minqty, Math.min(maxAllowed, val || 0));
            onChangeInput(coup.coupure.toString() as CoupureType, clamped * coup.perqty);
          };

          return (
            <div key={coup.coupure} className="flex justify-between gap-4 md:grid md:grid-cols-3">
              {/* Icon + Label */}
              <div className="flex items-center gap-3">
                <div className="flex justify-center items-center w-10 h-10 bg-neutral-50">
                  <GenericCoin value={coup.coupure} currency={currency} className="w-8 h-8" />
                </div>
                <div>
                  <p className="font-semibold leading-none">
                    {formatCashAmount(coup.coupure)} {currency}
                  </p>
                  {!bulk && (
                    <p className="text-xs sm:text-sm text-neutral-500">
                      {t('order_and_pickup.o_place_order.waffle_per')} {coup.perqty}{' '}
                      {t('order_and_pickup.o_place_order.pieces')}
                    </p>
                  )}
                </div>
              </div>

              {/* Min/Max + Input */}
              <div className="flex gap-2">
                <div className="flex flex-col justify-center items-end min-w-[80px] text-sm">
                  <p className="leading-none">
                    min: <span className="font-semibold">{coup.minqty}</span>
                  </p>
                  <p className="leading-none">
                    max: <span className="font-semibold">{maxAllowed}</span>
                  </p>
                </div>
                <Input
                  disabled={coup.perqty === 0}
                  value={displayValue}
                  onChange={e => handleChange(Math.round(parseFloat(e.target.value) || 0))}
                  type="number"
                  integerOnly
                  className="px-1 sm:px-3 md:min-w-[100px]"
                  min={coup.minqty}
                  max={maxAllowed}
                />
              </div>

              {/* Subtotal */}
              <div className="flex items-center justify-between">
                <div className="hidden gap-2 md:flex">
                  <X className="w-4" strokeWidth={1} />
                  <p className="font-medium">
                    {symbol} {formatCashAmount(coup.coupure * coup.perqty)}
                  </p>
                  <Equal className="w-4" strokeWidth={1} />
                </div>
                <p className="font-semibold">
                  {symbol} {formatCashAmount(calculateSubtotal(coup.coupure, inputQty))}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="w-full h-px bg-neutral-300 my-6" />

      <div className="flex justify-between font-semibold text-lg">
        <p>{t('order_and_pickup.o_place_order.total')}:</p>
        <div className="flex flex-col items-end">
          <p>{symbol} {calculateTotal()}</p>
          <p className="text-sm text-neutral-600">{calculateWeight()} kg</p>
        </div>
      </div>
    </>
  );
};

export default Coins;
